import React, { useState, useEffect } from "react";
import { Phone, Menu, X, Facebook, Instagram, Linkedin } from "lucide-react";
import { navLinks, businessInfo } from "../data/mockData";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-250 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg shadow-black/5"
          : "bg-white"
      }`}
      data-testid="site-header"
    >
      {/* Top Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 sm:h-24 md:h-28">
        {/* Left: phone + social icons */}
        <div className="flex items-center gap-3 sm:gap-4">
          <a
            href={`tel:${businessInfo.phone}`}
            className="flex items-center gap-1.5 sm:gap-2 text-brand-charcoal hover:text-brand-mint transition-colors text-xs sm:text-sm tracking-wide"
            data-testid="navbar-phone"
          >
            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="font-semibold hidden sm:inline">{businessInfo.phone}</span>
          </a>
          {businessInfo.facebook && (
            <a
              href={businessInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-charcoal hover:text-brand-mint transition-colors duration-200"
              aria-label="Facebook"
              data-testid="navbar-facebook"
            >
              <Facebook className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
            </a>
          )}
          {businessInfo.instagram && (
            <a
              href={businessInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-charcoal hover:text-brand-mint transition-colors duration-200"
              aria-label="Instagram"
              data-testid="navbar-instagram"
            >
              <Instagram className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
            </a>
          )}
          {businessInfo.linkedin && (
            <a
              href={businessInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-charcoal hover:text-brand-mint transition-colors duration-200"
              aria-label="LinkedIn"
              data-testid="navbar-linkedin"
            >
              <Linkedin className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
            </a>
          )}
        </div>

        {/* Logo Image */}
        <div className="text-center absolute left-1/2 -translate-x-1/2">
          <a
            href="#home"
            onClick={(e) => handleNavClick(e, "#home")}
            className="group block select-none"
            data-testid="navbar-logo"
          >
            <img
              src={businessInfo.logoImage}
              alt={`${businessInfo.name} logo`}
              className="h-16 sm:h-20 md:h-24 w-auto object-contain"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
        </div>

        {/* Right-side rating - desktop only */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-brand-charcoal font-bold">
            <span className="text-brand-gold-dark">★</span>
            <span>{businessInfo.rating.toFixed(1)}</span>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden text-brand-charcoal z-10"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-gray-100">
        <div className="flex items-center justify-center h-11">
          <div className="flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal hover:text-brand-mint transition-colors duration-200 relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[1.5px] after:bg-brand-gold hover:after:w-full after:transition-all after:duration-200"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Nav */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 pb-4 shadow-lg" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="block py-3 px-4 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal hover:text-brand-mint hover:bg-gray-50 transition-colors"
            >
              {link.label}
            </a>
          ))}
          {/* Rating badge in mobile menu */}
          <div className="flex items-center gap-2 py-3 px-4 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal">
            <span className="text-brand-gold-dark">★</span>
            <span>{businessInfo.rating.toFixed(1)} Google Rating</span>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
